// TEMP CORE-9199 remove after Oct 30
import { useEffect, useState, type ReactNode } from 'react'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import type {
  Section,
  ShadowReconciliation,
  ShadowSessionResponse,
} from './shadowSession.types'

const Block = <T,>({
  title,
  section,
  children,
}: {
  title: string
  section: Section<T>
  children: (data: T) => ReactNode
}) => (
  <section className="mb-6">
    <h3 className="mb-2 text-lg font-semibold">{title}</h3>
    {'error' in section ? (
      <p className="text-red-600">Failed: {section.error}</p>
    ) : (
      <div className="overflow-x-auto">{children(section.data)}</div>
    )}
  </section>
)

const Json = ({ value }: { value: unknown }) => (
  <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-neutral-100 p-2 text-xs">
    {JSON.stringify(value, null, 2)}
  </pre>
)

const Head = ({ columns }: { columns: string[] }) => (
  <thead>
    <tr>
      {columns.map((column) => (
        <th key={column} className={cell}>
          {column}
        </th>
      ))}
    </tr>
  </thead>
)

const cell = 'border px-2 py-1 text-left text-sm'

const RECON_OUTCOME_LABELS: Record<string, string> = {
  success: 'matched',
  fail: "balances didn't match the books",
  already_reconciled: 'month already matched by another statement',
  slot_conflict: 'another statement closed the month at a different balance',
  no_prior_recon: "the month before isn't done",
  wrong_period: "statement period doesn't sit inside the month",
  no_report: 'no bookkeeping month set up',
  invalid_period: 'statement dates unclear',
  books_complete: 'month already closed',
  process_mismatch: 'month is in a different bookkeeping process',
  later_complete: 'a later month is already closed',
  stale_anchor: 'prior month changed during the match',
  discontinuous_opening: 'opening balance ≠ prior close',
  already_covered: 'already reconciled by a longer period',
}

const STATUS_CLASS: Record<ShadowReconciliation['status'], string> = {
  complete: 'bg-success-25 text-success-300',
  incomplete: 'bg-danger-25 text-danger-300',
  blocked: 'bg-warning-25 text-warning-300',
}

const toDay = (iso: string | null): string =>
  iso
    ? DateTime.fromISO(iso, { zone: 'utc' }).toFormat(
        DATE_FORMATS_LUXON.YEAR_MONTH_DAY
      )
    : '-'

const toStatementReason = ({
  reconOutcome,
  gateDecision,
}: NonNullable<ShadowReconciliation['statement']>): string => {
  if (gateDecision === 'route_review') return 'route_review: bookkeeper review'
  if (reconOutcome === null) return 'read incomplete, no match run'
  return `${reconOutcome}: ${RECON_OUTCOME_LABELS[reconOutcome] ?? 'unknown outcome'}`
}

const toAutoReason = (recon: ShadowReconciliation): string => {
  if (!recon.autoReconAttemptedAt) return 'not attempted'
  if (recon.autoReconFailureCount === 0)
    return `${recon.autoReconStatus ?? 'no status'}, last tried ${toDay(recon.autoReconAttemptedAt)}`
  return `failed ${recon.autoReconFailureCount}×, first on ${toDay(recon.autoReconFirstFailedAt)}, last tried ${toDay(recon.autoReconAttemptedAt)}`
}

const ReconReasons = ({ recon }: { recon: ShadowReconciliation }) => {
  if (recon.status === 'complete') return null
  const blockedOn = recon.blockedOnReconciliation
  const unlinkedBlock = recon.status === 'blocked' && !blockedOn
  if (
    !blockedOn &&
    !unlinkedBlock &&
    !recon.statement &&
    !recon.autoReconAttemptedAt
  )
    return <i className="text-neutral-500">nothing attempted yet</i>
  return (
    <div className="space-y-0.5">
      {unlinkedBlock && (
        <div>
          <b>blocked:</b> no earlier reconciliation recorded
        </div>
      )}
      {blockedOn && (
        <div>
          <b>blocked:</b> waiting on {toDay(blockedOn.startingBalanceDate)} →{' '}
          {toDay(blockedOn.endingBalanceDate)} ({blockedOn.status})
        </div>
      )}
      {recon.statement && (
        <div>
          <b>statement:</b> {toStatementReason(recon.statement)}
        </div>
      )}
      <div>
        <b>auto:</b> {toAutoReason(recon)}
      </div>
    </div>
  )
}

const useSecondsAgo = (iso: string): number => {
  const [now, setNow] = useState(DateTime.now())
  useEffect(() => {
    const id = setInterval(() => setNow(DateTime.now()), 1000)
    return () => clearInterval(id)
  }, [])
  return Math.max(
    0,
    Math.round(now.diff(DateTime.fromISO(iso), 'seconds').seconds)
  )
}

export const JourneyView = ({ data }: { data: ShadowSessionResponse }) => {
  const secondsAgo = useSecondsAgo(data.fetchedAt)
  return (
    <div>
      <p className="mb-4 text-sm text-neutral-500">
        Updated {secondsAgo}s ago (refreshes every 10s)
      </p>

      <Block title="User" section={data.header}>
        {(header) => (
          <div className="space-y-1">
            <p className="font-semibold">{header.email}</p>
            {header.onboardingItemsCompletedAt && (
              <p className="rounded bg-amber-100 p-2 text-amber-900">
                Warning: onboarding already stamped complete at{' '}
                {header.onboardingItemsCompletedAt}
              </p>
            )}
            <p>
              enable-haven-cubk: <b>{String(header.flags.enableHavenCubk)}</b> ·
              presort-enabled: <b>{String(header.flags.presortEnabled)}</b>
            </p>
          </div>
        )}
      </Block>

      <Block title="Current step" section={data.steps}>
        {(steps) => {
          if (steps.refusal) {
            return (
              <p className="rounded bg-amber-100 p-2 text-amber-900">
                Catch-up cannot be assessed: <b>{steps.refusal}</b>
              </p>
            )
          }
          return (
            <ol className="space-y-1">
              {steps.steps.map(({ step, done }) => {
                const current = step === steps.currentStep
                return (
                  <li
                    key={step}
                    className={current ? 'font-bold text-blue-700' : undefined}
                  >
                    {done ? '✓' : '·'} {step}
                    {current && '  ← CURRENT'}
                  </li>
                )
              })}
            </ol>
          )
        }}
      </Block>

      <Block
        title="Empty months awaiting the user"
        section={
          'data' in data.steps ? data.steps.data.emptyMonths : data.steps
        }
      >
        {(accounts) =>
          accounts.length === 0 ? (
            <p>none</p>
          ) : (
            <ul>
              {accounts.map((account) => (
                <li key={account.financialAccountId}>
                  {account.name} {account.mask && `•${account.mask}`}:{' '}
                  {account.months.join(', ')}
                </li>
              ))}
            </ul>
          )
        }
      </Block>

      <Block title="Close" section={data.close}>
        {(close) => (
          <div className="space-y-2">
            <p>
              ready: <b>{String(close.ready)}</b> · books marked complete:{' '}
              <b>{String(close.alreadyComplete)}</b>
              {close.refusal && (
                <>
                  {' '}
                  · refusal: <b>{close.refusal}</b>
                </>
              )}
            </p>
            <p>
              missing statements: {String(close.missingStatements)} ·
              clarification transactions: {close.clarificationTransactions}
            </p>
            <div>
              <b>Books can&apos;t close case:</b>{' '}
              {close.openCase
                ? `#${close.openCase.id} (${close.openCase.status}) codes: ${
                    close.openCase.guardrailCodes.join(', ') || 'none recorded'
                  }`
                : 'none'}
            </div>
            <div>
              <b>
                Guardrail preview (assumes the user answers &quot;no&quot; to
                the loan question):
              </b>
              {close.guardrailPreview.length === 0 ? (
                <p>none would fire</p>
              ) : (
                close.guardrailPreview.map((hit) => (
                  <pre
                    key={hit.code}
                    className="whitespace-pre-wrap rounded bg-red-50 p-2 text-xs"
                  >
                    {hit.code}: {hit.summary}
                  </pre>
                ))
              )}
            </div>
            <table className="border-collapse">
              <Head
                columns={[
                  'month',
                  'closed',
                  'unconfirmed',
                  'unreconciled accts',
                  'clarification',
                  'complete',
                ]}
              />
              <tbody>
                {close.months.map((month) => (
                  <tr key={month.month}>
                    <td className={cell}>{month.month}</td>
                    <td className={cell}>{String(month.alreadyClosed)}</td>
                    <td className={cell}>{month.unconfirmedCount}</td>
                    <td className={cell}>
                      {month.unreconciledAccountIds.join(', ')}
                    </td>
                    <td className={cell}>{month.clarificationPendingCount}</td>
                    <td className={cell}>{String(month.complete)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block title="Accounts" section={data.accounts}>
        {(accounts) => (
          <table className="border-collapse">
            <Head
              columns={[
                'id',
                'institution',
                'name',
                'type',
                'sync',
                'plaid',
                'reconnect?',
                'use',
              ]}
            />
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td className={cell}>{account.id}</td>
                  <td className={cell}>{account.plaidInstitutionName}</td>
                  <td className={cell}>
                    {account.name} {account.mask && `•${account.mask}`}
                  </td>
                  <td className={cell}>
                    {account.type}/{account.subtype}
                  </td>
                  <td className={cell}>{account.transactionSyncState}</td>
                  <td className={cell}>
                    {account.plaidItem
                      ? `${account.plaidItem.transactionSyncStatus ?? ''} ${account.plaidItem.syncErrorClassification ?? ''}`
                      : 'manual'}
                  </td>
                  <td className={cell}>
                    {account.needsReconnection ? 'YES' : ''}
                  </td>
                  <td className={cell}>
                    {account.primaryUseEffective ?? account.primaryUse}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      <Block title="Reconciliations" section={data.reconciliations}>
        {(accounts) => (
          <div className="space-y-4">
            {accounts.map((account) => (
              <div key={account.id}>
                <p className="mb-1 font-semibold">
                  {account.plaidInstitutionName ?? 'manual'} · {account.name}{' '}
                  {account.mask && `•${account.mask}`}{' '}
                  <span className="font-normal text-neutral-500">
                    account {account.id}
                  </span>
                </p>
                {account.reconciliations.length === 0 ? (
                  <p className="italic text-neutral-500">none</p>
                ) : (
                  <table className="border-collapse">
                    <Head
                      columns={[
                        'month',
                        'start → end',
                        'status',
                        'created by',
                        "why it isn't complete",
                      ]}
                    />
                    <tbody>
                      {account.reconciliations.map((recon) => (
                        <tr key={recon.id}>
                          <td className={cell}>{recon.month}</td>
                          <td className={`${cell} whitespace-nowrap`}>
                            {toDay(recon.startingBalanceDate)} →{' '}
                            {toDay(recon.endingBalanceDate)}
                          </td>
                          <td className={cell}>
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_CLASS[recon.status]}`}
                            >
                              {recon.status}
                            </span>
                          </td>
                          <td className={cell}>{recon.origin}</td>
                          <td className={`${cell} text-xs`}>
                            <ReconReasons recon={recon} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </Block>

      <Block title="Statements on file" section={data.statements}>
        {(statements) => (
          <table className="border-collapse">
            <Head columns={['month', 'source', 'reading', 'accounts']} />
            <tbody>
              {statements.map((statement) => (
                <tr key={statement.userDocumentId}>
                  <td className={cell}>{statement.statementMonth}</td>
                  <td className={cell}>{statement.sourceLabel}</td>
                  <td className={cell}>{statement.reading.stateLabel}</td>
                  <td className={cell}>
                    <Json value={statement.accounts} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      <Block title="Sort" section={data.sort}>
        {(rows) => (
          <table className="border-collapse">
            <Head
              columns={[
                'account',
                'sorted / total',
                '% personal',
                'sorting stamped',
              ]}
            />
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={
                    row.sorted >= 20 && (row.personalPct ?? 0) > 80
                      ? 'bg-red-50'
                      : undefined
                  }
                >
                  <td className={cell}>
                    {row.name} {row.mask && `•${row.mask}`}
                  </td>
                  <td className={cell}>
                    {row.sorted} / {row.total}
                  </td>
                  <td className={cell}>
                    {row.personalPct === null ? '-' : `${row.personalPct}%`}
                  </td>
                  <td className={cell}>
                    {row.separationCompletedAt ? 'yes' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Block>

      <Block title="Categorize" section={data.categorize}>
        {(categorize) => (
          <p>
            confirmed {categorize.confirmed} / {categorize.total}
          </p>
        )}
      </Block>
    </div>
  )
}
